'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { usePrevious } from '#pplx/frontend/common/hooks/usePrevious.js';
import { ComputerCommandQueryPlugin } from '#pplx/frontend/components/form/lexical/plugins/ComputerCommandQueryPlugin.js';
import { SlashCommandHintPlugin } from '#pplx/frontend/components/form/lexical/plugins/SlashCommandHintPlugin.js';
import { getSerializableEditorState } from '#pplx/frontend/components/form/lexical/plugins/SlashCommandHintPluginSupport.js';
import { convertFromMarkdownString, convertToMarkdownString, supportsLookbehind, } from '#pplx/frontend/components/form/lexical/utils/textConversion.js';
import { enterCommand } from '#pplx/frontend/components/form/ResizableInput/commands/enterCommand.js';
import { EXTERNAL_UPDATE_TAG, FOCUS_TAG, TAGS_TO_IGNORE, } from '#pplx/frontend/components/form/ResizableInput/constants.js';
import { useAutoExpansion } from '#pplx/frontend/components/form/ResizableInput/utils.js';
import { ComposerInput } from '#pplx/frontend/composer-sdk/ComposerInput.js';
import { useComposerInputCommands } from '#pplx/frontend/composer-sdk/hooks/useComposerInputCommands.js';
import { useComposerInputPlaceholder } from '#pplx/frontend/composer-sdk/hooks/useComposerInputPlaceholder.js';
import { useComposerInputTransforms } from '#pplx/frontend/composer-sdk/hooks/useComposerInputTransforms.js';
import { useComposerRootEventListeners } from '#pplx/frontend/composer-sdk/hooks/useComposerRootEventListeners.js';
import { useLexicalInitialConfig } from '#pplx/frontend/composer-sdk/hooks/useLexicalInitialConfig.js';
import { usePreHydrationClickFallback } from '#pplx/frontend/composer-sdk/hooks/usePreHydrationClickFallback.js';
import { ComposerContentEditable } from '#pplx/frontend/composer-sdk/lexical/ComposerContentEditable.js';
import { MentionIconRendererContext } from '#pplx/frontend/composer-sdk/lexical/MentionIconRendererContext.js';
import { NODES } from '#pplx/frontend/composer-sdk/lexical/nodes/Nodes.js';
import { composerStyles } from '#pplx/frontend/composer-sdk/styles.js';
import { useComposer } from '#pplx/frontend/composer-sdk/useComposer.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { ErrorBoundary } from '#pplx/frontend/platform-components/ErrorBoundary.js';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import classNames from 'classnames';
import { $addUpdateTag, $createTextNode, $getRoot, $getSelection, $isParagraphNode, $isRangeSelection, $isTextNode, $selectAll, $setSelection, } from 'lexical';
import { isEqual } from 'lodash-es';
import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, } from 'react';
const HistoryPlugin = Dynamic(async () => {
    const { HistoryPlugin } = await import('@lexical/react/LexicalHistoryPlugin');
    return { default: HistoryPlugin };
});
const OnChangePlugin = Dynamic(async () => {
    const { OnChangePlugin } = await import('@lexical/react/LexicalOnChangePlugin');
    return { default: OnChangePlugin };
});
const EditorRefPlugin = Dynamic(async () => {
    const { EditorRefPlugin } = await import('@lexical/react/LexicalEditorRefPlugin');
    return { default: EditorRefPlugin };
});
const ClickableLinkPlugin = Dynamic(async () => {
    const { ClickableLinkPlugin } = await import('@lexical/react/LexicalClickableLinkPlugin');
    return { default: ClickableLinkPlugin };
});
const MentionsPlugin = Dynamic(async () => {
    const { default: MentionsPluginDefault } = await import('#pplx/frontend/components/form/lexical/plugins/MentionsPlugin.js');
    return { default: MentionsPluginDefault };
});
const AutoLinkPlugin = Dynamic(async () => {
    const { default: AutoLinkPluginDefault } = await import('#pplx/frontend/composer-sdk/lexical/plugins/AutoLinkPlugin.js');
    return { default: AutoLinkPluginDefault };
});
const AUTO_LINK_SUPPRESS_IN = ['codeFence'];
const LinkPlugin = Dynamic(async () => {
    const { default: LinkPluginDefault } = await import('#pplx/frontend/composer-sdk/lexical/plugins/LinkPlugin.js');
    return { default: LinkPluginDefault };
});
const LinkMarkdownShortcutPlugin = Dynamic(async () => {
    const { LinkMarkdownShortcutPlugin } = await import('#pplx/frontend/composer-sdk/lexical/plugins/LinkMarkdownShortcutPlugin.js');
    return { default: LinkMarkdownShortcutPlugin };
});
/** Whether it's safe for a programmatic focus call to move focus (WCAG 9.2.4.3). */
function canStealFocus() {
    return !document.activeElement?.closest('[role="tablist"]');
}
const ResizableInput = ({ autoFocus = false, selectAllOnAutoFocus = false, placeholder = '', placeholderAnimation, placeholderClassName, disableInput = false, value, initialLayout = 'expanded', json, minRows, maxRows, onChange, onClick, onBlur, onFocus, onKeyDown, onBeforeInput, onPaste, isMobileStyle, isMobileUserAgent, testId, id, editorFocusRef, ariaLabel, mentionTypeaheadOptions = EMPTY_ARRAY, mentionIconRenderer, spacesCopyVariant, onMentionMenuOpen, onMentionMenuClose, onTriggerTypeahead, onMentionSearchQueryChange, slashCommandHintText, onSlashCommandSearchQueryChange, computerCommandQueryEnabled = false, computerCommandQueryPlaceholderText, onComputerCommandQueryChange, lexicalEditorRef, variant, ref, }) => {
    const containerRef = useRef(null);
    const [editorRef, setEditorRef] = useState(null);
    const setLexicalEditorRefs = useCallback((editor) => {
        setEditorRef(editor);
        if (lexicalEditorRef) {
            lexicalEditorRef.current = editor;
        }
    }, [lexicalEditorRef]);
    const { isActive, isComposing, isExpanded, setExpanded, setActive, setDisabled } = useComposer();
    const isComposingRef = useRef(isComposing);
    const isActiveRef = useRef(isActive);
    const valueRef = useRef('');
    const jsonRef = useRef(null);
    const autoFocusRef = useRef(autoFocus);
    const mentionsPluginRef = useRef(null);
    // Refs so that we don't reset the Lexical editor's callbacks
    const onChangeRef = useRef(onChange);
    const onKeyDownRef = useRef(onKeyDown);
    onChangeRef.current = onChange;
    onKeyDownRef.current = onKeyDown;
    isComposingRef.current = isComposing;
    isActiveRef.current = isActive;
    autoFocusRef.current = autoFocus;
    const prevDisabled = usePrevious(disableInput);
    const enterKeyBehavior = isMobileUserAgent ? 'newline' : 'submit';
    const placeholderClass = useMemo(() => classNames(...composerStyles.placeholder.base, {
        [composerStyles.placeholder.compact]: !isExpanded,
    }, placeholderClassName), [isExpanded, placeholderClassName]);
    //===========================================================================
    // CALLBACKS
    //===========================================================================
    const handleOnChange = useCallback((editorState, _editor, tags) => {
        const tagsList = [...tags];
        let currentValue = undefined;
        if (tagsList.length > 0 && tagsList.every((tag) => TAGS_TO_IGNORE.has(tag))) {
            return;
        }
        if (currentValue === undefined) {
            currentValue = convertToMarkdownString(getSerializableEditorState(editorState));
        }
        valueRef.current = currentValue ?? '';
        jsonRef.current = getSerializableEditorState(editorState);
        onChangeRef.current?.(valueRef.current, jsonRef.current);
    }, []);
    const focusAtEnd = useCallback(() => editorRef?.update(() => {
        $addUpdateTag(FOCUS_TAG);
        $getRoot().selectEnd();
    }), [editorRef]);
    const append = useCallback((text) => editorRef?.update(() => {
        const root = $getRoot();
        const node = root.getLastChild();
        if ($isParagraphNode(node)) {
            const textNode = $createTextNode(text);
            node.append(textNode);
            root.selectEnd();
        }
    }, 
    // Some callers read value/json immediately after append. Commit synchronously
    // so those reads describe the updated tree instead of stale editor JSON.
    { discrete: true }), [editorRef]);
    const trim = useCallback(() => editorRef?.update(() => {
        const root = $getRoot();
        const node = root.getLastChild();
        if ($isParagraphNode(node)) {
            const firstNode = node.getFirstChild();
            if ($isTextNode(firstNode)) {
                const text = firstNode.getTextContent();
                const trimmedText = text.trimStart();
                const trimmedLength = text.length - text.trimStart().length;
                if (trimmedLength) {
                    firstNode.spliceText(0, text.length, trimmedText, true);
                }
            }
            const lastNode = node.getLastChild();
            if ($isTextNode(lastNode)) {
                const text = lastNode.getTextContent();
                const trimmedText = text.trimEnd();
                const trimmedLength = text.length - trimmedText.length;
                if (trimmedLength) {
                    lastNode.spliceText(0, text.length, trimmedText, true);
                }
            }
        }
    }), [editorRef]);
    const inLine = useCallback((type) => {
        if (!editorRef)
            return true; // fail open
        return editorRef.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection))
                return true; // fail open
            if (!selection.isCollapsed())
                return false; // need empty selection
            // Get the DOM selection to work with visual line positions
            const domSelection = window.getSelection();
            if (!domSelection || domSelection.rangeCount === 0)
                return true; // fail open
            const range = domSelection.getRangeAt(0);
            let selectionRect = range.getBoundingClientRect();
            if (selectionRect.width === 0 && selectionRect.height === 0) {
                // when there's only whitespace in a contenteditable, getBoundingClientRect()
                // returns zeros because whitespace collapses and doesn't produce a visible
                // rendered box--temporarily add a zero-width space to get a valid rect
                const tempNode = document.createTextNode('\u200B');
                range.insertNode(tempNode);
                selectionRect = range.getBoundingClientRect();
                tempNode.remove();
            }
            const editorRect = editorRef
                .getElementByKey($getRoot().getFirstChild()?.getKey() ?? '')
                ?.getBoundingClientRect();
            if (!editorRect)
                return true; // fail open
            // Allow for some tolerance (a few pixels) to account for line height variations
            const lineHeightTolerance = 4;
            if (type === 'first') {
                // Check if the selection is on the same visual line as the start of content
                const onSameLineAsStart = Math.abs(selectionRect.top - editorRect.top) <= lineHeightTolerance;
                return onSameLineAsStart;
            }
            else {
                // Check if the selection is on the same visual line as the end of content
                const onSameLineAsEnd = Math.abs(selectionRect.bottom - editorRect.bottom) <= lineHeightTolerance;
                return onSameLineAsEnd;
            }
        });
    }, [editorRef]);
    const handleMentionMenuOpen = useCallback(() => {
        onMentionMenuOpen?.();
    }, [onMentionMenuOpen]);
    const handleMentionMenuClose = useCallback(() => {
        onMentionMenuClose?.();
    }, [onMentionMenuClose]);
    const handleEarlyClick = usePreHydrationClickFallback({
        editor: editorRef,
        onClick,
    });
    //===========================================================================
    // EFFECTS
    //===========================================================================
    useImperativeHandle(ref, () => ({
        append,
        trim,
        focusAtEnd,
        isFocused: () => isActiveRef.current,
        inLine,
        focus: () => editorRef?.focus(),
        blur: () => editorRef?.blur(),
        scrollIntoView: (arg) => editorRef?.getRootElement()?.scrollIntoView(arg),
        scrollToEnd: () => editorRef?.getRootElement()?.scrollIntoView({
            block: 'end',
            behavior: 'auto',
        }),
        element: () => containerRef.current?.element() ?? null,
        mwebFocusRoot: (opts) => editorRef?.getRootElement()?.focus(opts),
        get scrollHeight() {
            return editorRef?.getRootElement()?.scrollHeight ?? 0;
        },
        get value() {
            const editorState = editorRef?.getEditorState();
            return editorState ? convertToMarkdownString(getSerializableEditorState(editorState)) : '';
        },
        set value(_) {
            throw new Error('setValue is not supported in Lexical');
        },
        get json() {
            const editorState = editorRef?.getEditorState();
            return editorState ? getSerializableEditorState(editorState) : undefined;
        },
    }), [append, focusAtEnd, trim, editorRef, inLine]);
    useAutoExpansion(value, initialLayout, isMobileStyle, isExpanded, setExpanded);
    useEffect(() => {
        let rafId;
        if (!isMobileUserAgent && editorRef && autoFocus) {
            rafId = window.requestAnimationFrame(() => {
                if (!canStealFocus()) {
                    return;
                }
                if (selectAllOnAutoFocus) {
                    editorRef.update(() => {
                        $addUpdateTag(FOCUS_TAG);
                        $selectAll();
                    });
                }
                else {
                    focusAtEnd();
                }
                setActive(true);
            });
        }
        return () => {
            if (rafId !== undefined) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [isMobileUserAgent, autoFocus, selectAllOnAutoFocus, setActive, editorRef, focusAtEnd]);
    useEffect(() => {
        let timerId;
        if (prevDisabled !== disableInput && !disableInput && !isMobileUserAgent && autoFocus) {
            timerId = setTimeout(() => {
                if (!canStealFocus()) {
                    return;
                }
                focusAtEnd();
            }, 200);
        }
        return () => {
            if (timerId !== undefined) {
                clearTimeout(timerId);
            }
        };
    });
    useComposerRootEventListeners({
        editor: editorRef,
        enterKeyBehavior,
        onFocus,
        onBlur,
        onPaste,
        onBeforeInput,
        onClick,
        onKeyDown,
    });
    // RichTextPlugin gives us correct paste/import behavior around decoration
    // nodes; these hooks preserve the legacy ResizableInput contract by keeping
    // the result visually plain, single-paragraph, and decorator-node friendly.
    useComposerInputCommands({ editor: editorRef });
    useComposerInputTransforms({ editor: editorRef });
    useLayoutEffect(() => {
        if (editorRef)
            editorRef.setEditable(!disableInput);
        setDisabled(disableInput);
    }, [disableInput, editorRef, setDisabled]);
    useLayoutEffect(() => {
        if (!editorRef) {
            return;
        }
        // Updating via json will preserve the rich(er) text state, but
        // if all we have is a value string, we can make that work, too
        if (value && json && !isEqual(json, jsonRef.current)) {
            // QueueMicrotask is not supported in all environments we support
            Promise.resolve().then(() => {
                // Internally this calls flushSync, so it needs to be called async
                editorRef.setEditorState(editorRef.parseEditorState(json), {
                    tag: EXTERNAL_UPDATE_TAG,
                });
                if (autoFocusRef.current) {
                    focusAtEnd();
                }
                jsonRef.current = editorRef.getEditorState().toJSON();
                valueRef.current = convertToMarkdownString(jsonRef.current);
            });
        }
        else if (value && valueRef.current !== value) {
            editorRef.update(() => {
                $addUpdateTag(EXTERNAL_UPDATE_TAG);
                const root = $getRoot();
                root.clear();
                if (supportsLookbehind) {
                    convertFromMarkdownString(value);
                }
                else {
                    root.append($createTextNode(value));
                }
                if (autoFocusRef.current) {
                    root.selectEnd();
                }
            }, { discrete: true });
            jsonRef.current = editorRef.getEditorState().toJSON();
            valueRef.current = convertToMarkdownString(jsonRef.current);
        }
        else if (!value && valueRef.current !== value) {
            editorRef.update(() => {
                $addUpdateTag(EXTERNAL_UPDATE_TAG);
                const root = $getRoot();
                root.clear();
                if (autoFocusRef.current) {
                    root.selectEnd();
                }
                else if (editorRef.getRootElement() !== document.activeElement) {
                    // No autoFocus and not in editor--clear new selection from `.clear`
                    $setSelection(null);
                }
            }, { discrete: true });
            jsonRef.current = editorRef.getEditorState().toJSON();
            valueRef.current = convertToMarkdownString(jsonRef.current);
        }
    }, [value, json, editorRef, focusAtEnd]);
    useEffect(() => {
        if (!editorRef)
            return;
        // Keep Enter handling local to the legacy input: it has to defer to the legacy
        // MentionsPlugin handle before blocking Lexical's default paragraph insertion.
        const enterCommandCleanup = enterCommand(editorRef, {
            isComposingRef,
            enterKeyBehavior,
            mentionsPluginRef,
            onKeyDownRef,
        });
        return () => {
            enterCommandCleanup();
        };
    }, [editorRef, enterKeyBehavior]);
    //===========================================================================
    // RENDER
    //===========================================================================
    const initialConfig = useLexicalInitialConfig({
        nodes: variant === 'pplxtext' ? NODES : EMPTY_ARRAY,
        editable: !disableInput,
    });
    const { currentPlaceholder, placeholderElement } = useComposerInputPlaceholder({
        placeholder,
        animation: placeholderAnimation,
        compact: !isExpanded,
        className: placeholderClass,
    });
    const contentEditable = useMemo(() => (_jsx(ComposerContentEditable, { minRows: minRows, maxRows: maxRows, "aria-label": ariaLabel, "aria-placeholder": currentPlaceholder, placeholder: placeholderElement, "data-test-id": testId, id: id, ref: editorFocusRef })), [
        minRows,
        maxRows,
        ariaLabel,
        id,
        editorFocusRef,
        currentPlaceholder,
        placeholderElement,
        testId,
    ]);
    return useMemo(() => (_jsx(ComposerInput, { isExpanded: isExpanded, minRows: minRows, onClick: handleEarlyClick, ref: containerRef, children: _jsx(MentionIconRendererContext.Provider, { value: mentionIconRenderer, children: _jsxs(LexicalComposer, { initialConfig: initialConfig, children: [_jsx(RichTextPlugin, { contentEditable: contentEditable, ErrorBoundary: ErrorBoundary }), _jsx(HistoryPlugin, {}), _jsx(OnChangePlugin, { onChange: handleOnChange, ignoreSelectionChange: true }), _jsx(EditorRefPlugin, { editorRef: setLexicalEditorRefs }), variant === 'pplxtext' && (_jsxs(_Fragment, { children: [_jsx(SlashCommandHintPlugin, { hintText: slashCommandHintText, onSearchQueryChange: onSlashCommandSearchQueryChange }), _jsx(ComputerCommandQueryPlugin, { enabled: computerCommandQueryEnabled, placeholderText: computerCommandQueryPlaceholderText ?? slashCommandHintText ?? '', onQueryChange: onComputerCommandQueryChange }), _jsx(MentionsPlugin, { ref: mentionsPluginRef, onClose: handleMentionMenuClose, onOpen: handleMentionMenuOpen, options: mentionTypeaheadOptions, spacesCopyVariant: spacesCopyVariant, onTrigger: onTriggerTypeahead, onSearchQueryChange: onMentionSearchQueryChange }), _jsx(AutoLinkPlugin, { suppressIn: AUTO_LINK_SUPPRESS_IN }), _jsx(LinkPlugin, {}), _jsx(ClickableLinkPlugin, {}), _jsx(LinkMarkdownShortcutPlugin, {})] }))] }) }) })), [
        isExpanded,
        minRows,
        handleEarlyClick,
        initialConfig,
        contentEditable,
        handleOnChange,
        setLexicalEditorRefs,
        handleMentionMenuClose,
        handleMentionMenuOpen,
        mentionTypeaheadOptions,
        spacesCopyVariant,
        mentionIconRenderer,
        onTriggerTypeahead,
        onMentionSearchQueryChange,
        computerCommandQueryEnabled,
        computerCommandQueryPlaceholderText,
        onComputerCommandQueryChange,
        slashCommandHintText,
        onSlashCommandSearchQueryChange,
        variant,
    ]);
};
export const PlaintextInput = (props) => {
    return _jsx(ResizableInput, { ...props, variant: "plaintext" });
};
export const PplxtextInput = (props) => {
    return _jsx(ResizableInput, { ...props, variant: "pplxtext" });
};
//# sourceMappingURL=Input.js.map